import { IGameConfigurationService, IChatConfigurationService, IConfigurationService } from '@/app-modules/config/config-service-interface';
import { IGameResponseViewModel, IArcadeGameViewModel, IChatRoomGameViewModel, ChatRoomConfiguration, IGamesResponseViewModel, IGameClubViewModel, IGameChatRoomViewModel, ICmChatResponseViewModel, ConfigurationPaths, IChatModsConfiguration, ICsChatResponseViewModel } from '@/app-modules/config/models';
import { IHttpService, IHttpRequest } from '@/app-modules/http';
import { injectable, inject } from 'inversify';
import { SERVICE_IDENTIFIERS } from '@/app-modules/_ioc/service-identifiers';
import { generateGuid } from '@/helper';
import { compareGameNames } from '@/components/game/_domain/helpers';
import _ from 'lodash';
import { ITogglesService } from '@/app-modules/config';

@injectable()
class GameConfigurationService implements IGameConfigurationService, IChatConfigurationService {
  private gameConfiguration: IGamesResponseViewModel;
  private arcadeGameConfiguration: IArcadeGameViewModel[];
  private chatRoomConfiguration: ChatRoomConfiguration[];
  private cmChatConfig: ICmChatResponseViewModel | null = null;
  private csChatConfig: ICsChatResponseViewModel | null = null;

  constructor(
    @inject(SERVICE_IDENTIFIERS.IHttpService) private readonly httpService: IHttpService,
    @inject(SERVICE_IDENTIFIERS.IConfigurationService) private readonly configurationService: IConfigurationService,
    @inject(SERVICE_IDENTIFIERS.ITogglesService) private readonly toggleService: ITogglesService
  ) {
  }

  public async getGameConfiguration(): Promise<IGameResponseViewModel[]> {
    if (!this.gameConfiguration) {
      const gameConfig = await this.configurationService.getGameConfiguration();
      if (gameConfig.Games.length === 0) {
        throw new Error('Game configuration is not available.');
      }
      this.generateGuidsForGameAndClubs(gameConfig);
      this.gameConfiguration = gameConfig;
    }
    return this.gameConfiguration.Games.sort(this.compareGameModels);
  }

  public async getAllChatooms(): Promise<IChatRoomGameViewModel[]> {
    const chatRooms: IChatRoomGameViewModel[] = [];
    await this.getGameConfiguration();

    this.gameConfiguration.Games.forEach((game) => game.Clubs.forEach((club) => club.ChatRooms.forEach((room) => chatRooms.push({
      Id: room.Id,
      RoomName: room.Name,
      GameName: game.Name,
      ClubId: club.Id,
      ClubName: club.Name
    }))));

    this.gameConfiguration.ArcadeChatrooms.ChatRooms.forEach((room) => chatRooms.push({
      Id: room.Id,
      RoomName: room.Name,
      GameName: this.gameConfiguration.ArcadeChatrooms.Name
    }));

    return chatRooms;
  }

  public async getArcadeGameConfiguration(): Promise<IArcadeGameViewModel[]> {
    if (!this.arcadeGameConfiguration) {
      const config = await this.configurationService.getConfiguration();
      const arcadeGameNames = this.getArcadeGameNamesFromConfiguration(config);
      this.arcadeGameConfiguration = arcadeGameNames.map((arcadeGameName) => {
        const trimmedName = arcadeGameName.trim();
        const skins = this.getArcadeGameSkinsFromConfiguration(config, trimmedName);
        const displayName = skins.length === 1 ? skins[0] : trimmedName;
        return {
          Name: trimmedName,
          DisplayName: displayName,
          ModalGuid: generateGuid()
        };
      }).sort(this.compareArcadeGameModels);
    }
    return this.arcadeGameConfiguration;
  }

  private getArcadeGameNamesFromConfiguration(config: IChatModsConfiguration): string[] {
    const arcadeGameNames = config.getConfigurationByPath(ConfigurationPaths.arcadegames.names) as string;
    return arcadeGameNames ? arcadeGameNames.split(',').map((o) => o.trim()) : [];
  }

  private getArcadeGameSkinsFromConfiguration(config: IChatModsConfiguration, arcadeGameName: string): string[] {
    const arcadeGameConfigPaths = ConfigurationPaths.forArcadeGame(arcadeGameName);
    const skins = config.getConfigurationByPath(arcadeGameConfigPaths.skins) as string;
    return skins.split(',').map((o) => o.trim());
  }

  public async getClubNameForGameNameAndClubId(gameName: string, clubId: number): Promise<string> {
    const gameConfiguration = await this.getGameConfigurationForGameName(gameName);
    return this.getClubNameFromGameConfigurationForClubId(gameConfiguration, clubId);
  }

  public async gameHasSingleActiveClub(gameName: string): Promise<boolean> {
    const gameConfiguration = await this.getGameConfigurationForGameName(gameName);
    if (gameConfiguration && gameConfiguration.Clubs) {
      return gameConfiguration && gameConfiguration.Clubs.length === 1;
    }
    return false;
  }

  public async isGameAfreeGame(gameName: string): Promise<boolean> {
    let gameConfiguration = await this.getGameConfigurationForGameName(gameName);
    if (gameConfiguration) {
      return gameConfiguration.IsFreeGame === true;
    }

    // SuperSpins has the wrong name in game management
    // this is to force the free game check for SuperSpins
    if (gameName === 'SuperSpins'){
      return true;
    }

    // the game engine name almost always matches the name in game engine management but if we could not find this
    // do one final check for the freegame name by removing some commonly appended words to the name
    const sanitisedGameName = this.sanitiseFreeGameName(gameName);

    if (sanitisedGameName === gameName) {
      return false;
    }

    gameConfiguration = await this.getGameConfigurationForGameName(sanitisedGameName);

    if (gameConfiguration) {
      return gameConfiguration.IsFreeGame === true;
    }

    return false;
  }

  private sanitiseFreeGameName(gameName: string): string {
    return gameName.replace(/game/ig, '');
  }

  private async getGameConfigurationForGameName(gameName: string): Promise<IGameResponseViewModel> {
    await this.getGameConfiguration();
    return this.gameConfiguration.Games.find((game) => compareGameNames(game.Name, gameName) === 0);
  }

  private getClubNameFromGameConfigurationForClubId(gameConfiguration: IGameResponseViewModel, clubId: number): string {
    if (gameConfiguration && gameConfiguration.Clubs) {
      const clubConfiguration = gameConfiguration.Clubs.find((club) => club.Id === clubId);
      if (clubConfiguration) {
        return clubConfiguration.Name;
      }
    }
    return null;
  }

  public async getChatConfiguration(): Promise<ChatRoomConfiguration[]> {
    if (!this.chatRoomConfiguration) {
      await this.getGameConfiguration();
      this.chatRoomConfiguration = this.getChatRoomConfigurationFromGameConfig();
    }
    return this.chatRoomConfiguration;
  }

  public async getCmChatConfiguration(): Promise<ICmChatResponseViewModel> {
    if (!this.cmChatConfig) {
      return this.cmChatConfig = await this.getCmChatConfigurationFromServer();
    }
    return this.cmChatConfig;
  }

  public async getCsChatConfiguration(): Promise<ICsChatResponseViewModel> {
    if (!this.csChatConfig) {
      return this.csChatConfig = await this.getCsChatConfigurationFromServer();
    }
    return this.csChatConfig;
  }

  private async getCmChatConfigurationFromServer(): Promise<ICmChatResponseViewModel> {
    const httpRequest: IHttpRequest = {
      Url: '/configuration/cm-chat'
    } as IHttpRequest;
    const result = await this.httpService.get(httpRequest, true);
    if (result.status !== 200) {
      throw new Error('Could not retrieve CM Chat configuration.');
    }

    const cm = {
      ChatServerConfigKey: result.data.chatServerConfigKey,
      Name: result.data.name,
      Rooms: [
        {
          Id: result.data.rooms[0].id,
          Name: result.data.rooms[0].name,
        }
      ]
    };

    return cm as ICmChatResponseViewModel;
  }

  private async getCsChatConfigurationFromServer(): Promise<ICsChatResponseViewModel> {
    const httpRequest: IHttpRequest = {
      Url: '/configuration/cs-chat'
    } as IHttpRequest;
    const result = await this.httpService.get(httpRequest, true);
    if (result.status !== 200) {
      throw new Error('Could not retrieve CS Chat configuration.');
    }

    const cs = {
      ChatServerConfigKey: result.data.chatServerConfigKey,
      Name: result.data.name,
      Rooms: [
        {
          Id: result.data.rooms[0].id,
          Name: result.data.rooms[0].name,
        }
      ]
    };

    return cs as ICmChatResponseViewModel;
  }

  private generateGuidsForGameAndClubs(gameConfig: IGamesResponseViewModel) {
    gameConfig.Games.forEach((item) => {
      item.ModalGuid = generateGuid();
      item.Clubs.forEach((club) => club.ModalGuid = generateGuid());
    });
    gameConfig.ArcadeChatrooms.ModalGuid = generateGuid();
    if (gameConfig.ArcadeChatrooms.ChatRooms) {
      gameConfig.ArcadeChatrooms.ChatRooms.forEach((room) => room.ModalGuid = generateGuid());
    }
  }

  private getChatRoomConfigurationFromGameConfig(): ChatRoomConfiguration[] {
    const arcadeChatRooms = this.gameConfiguration.ArcadeChatrooms;
    let roomConfig =
      this.gameConfiguration.Games
        .filter((game) => game.Clubs.length > 0)
        .map((game) => new ChatRoomConfiguration(game.Name, game.FrontEndName || game.Name, this.extractChatRoomsFromClubs(game.Clubs)));

    if (arcadeChatRooms.ChatRooms) {
      roomConfig = roomConfig.concat(new ChatRoomConfiguration(arcadeChatRooms.Name, arcadeChatRooms.Name, arcadeChatRooms.ChatRooms.map((room) => Object.assign({}, room, { IsArcade: true }))));
    }

    return roomConfig;
  }

  private extractChatRoomsFromClubs(clubs: IGameClubViewModel[]): IGameChatRoomViewModel[] {
    const rooms = this.filterDistinctChatRoomsByChatRoomId(_.flatMap(clubs, (club) => club.ChatRooms));
    rooms.forEach((chatRoom) => chatRoom.ModalGuid = generateGuid());
    if (this.useRoomIdOrdering(clubs)) {
      return rooms.sort(this.compareChatRoomModelsRoomIds);
    }
    return rooms.sort(this.compareChatRoomModels);
  }

  private useRoomIdOrdering(clubs: IGameClubViewModel[]): boolean {
    if (clubs && clubs.length === 1) {
      return clubs[0].Name.startsWith('Britains Biggest Bingo');
    }

    return false;
  }

  private filterDistinctChatRoomsByChatRoomId(chatRooms: IGameChatRoomViewModel[]): IGameChatRoomViewModel[] {
    const rooms: IGameChatRoomViewModel[] = [];
    chatRooms.forEach((chatRoom) => {
      if (!rooms.some((room) => room.Id === chatRoom.Id)) {
        rooms.push(Object.assign({}, chatRoom));
      }
    });
    return rooms;
  }

  private compareArcadeGameModels(arcadeGameModelA: IArcadeGameViewModel, arcadeGameModelB: IArcadeGameViewModel): number {
    const displayNameA = arcadeGameModelA.DisplayName;
    const displayNameB = arcadeGameModelB.DisplayName;
    return displayNameA.localeCompare(displayNameB);
  }

  private compareChatRoomModels(chatRoomA: IGameChatRoomViewModel, chatRoomB: IGameChatRoomViewModel): number {
    const nameA = chatRoomA.Name;
    const nameB = chatRoomB.Name;
    return nameA.localeCompare(nameB);
  }

  private compareChatRoomModelsRoomIds(chatRoomA: IGameChatRoomViewModel, chatRoomB: IGameChatRoomViewModel): number {
    const idA = chatRoomA.Id;
    const idB = chatRoomB.Id;
    return idA - idB;
  }

  private compareGameModels(gameModelA: IGameResponseViewModel, gameModelB: IGameResponseViewModel): number {
    const nameA = gameModelA.FrontEndName || gameModelA.Name;
    const nameB = gameModelB.FrontEndName || gameModelB.Name;
    return nameA.localeCompare(nameB);
  }
}

export { GameConfigurationService };



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/app-modules/config/game-config-service.ts